import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import { COMPANY_STATISTICS } from 'events/types';
import { getCompanyStatistics } from 'services/dashboard';
import type { DashboardMetric } from 'types';
import useTranslation from 'customHooks/translate';
import { ERefetchType } from 'enums';
import { OUTGOING_INVOICES_ID } from 'consts';

import { CURRENCY_CONFIG } from './currency-config';
import {
  toChartPoints,
  calculateTrend,
  getTrendColor,
  getTrendIcon,
  formatValue,
} from './utils';

function MetricCard({
  metric,
  translation,
}: {
  metric: DashboardMetric;
  translation;
}) {
  const chartData = toChartPoints(metric.chartData);
  const { trend, percentage } = calculateTrend(metric.chartData);
  const trendColor = getTrendColor(trend);

  const formatTooltipValue = (value: number) => {
    const symbol = metric.currency
      ? CURRENCY_CONFIG[metric.currency]?.symbol
      : undefined;

    return symbol
      ? `${symbol} ${value.toLocaleString()}`
      : value.toLocaleString();
  };

  return (
    <div className="flex flex-col gap-[12px] rounded-[6px] border border-[#F5F7F9] p-[16px] transition-shadow hover:shadow-md">
      <div className="flex flex-col gap-[8px]">
        <p className="text-[14px] text-[#292D32]">{metric.label}</p>
        <p className="text-[28px] font-medium text-black">
          {translation.reports.Today} {formatValue(metric)}
        </p>
        <div className="flex flex-wrap items-center gap-x-[8px] gap-y-[2px]">
          <span
            className="text-[12px] font-medium"
            style={{ color: trendColor }}
          >
            {getTrendIcon(trend)} {percentage.toFixed(1)}%
          </span>
          <span className="text-[12px] text-[#6B7280]">
            {translation.dashboard.StatisticsOverPeriod}
          </span>
          <span className="text-[12px] text-[#6B7280]">
            · {translation.reports.Total}:{' '}
            {formatTooltipValue(
              metric.chartData?.reduce((a, b) => a + b, 0) ?? 0
            )}
          </span>
        </div>
      </div>
      {chartData?.length > 0 && (
        <div className="h-[72px] w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 4, right: 4, left: 4, bottom: 4 }}
            >
              <defs>
                <linearGradient
                  id={`gradient-${metric.id}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={trendColor} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={trendColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" hide />
              <YAxis hide domain={['auto', 'auto']} />
              <Tooltip
                formatter={(value: number, name: string) => [
                  formatTooltipValue(value),
                  metric?.label || name,
                ]}
                labelFormatter={() => ''}
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 6,
                  border: '1px solid #F5F7F9',
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={trendColor}
                strokeWidth={1.5}
                fill={`url(#gradient-${metric.id})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

const Statistics = () => {
  const translation = useTranslation();

  const {
    data: metrics,
    isLoading,
    isError,
    error,
  } = useQuery<
    Awaited<ReturnType<typeof getCompanyStatistics>>,
    AxiosError,
    DashboardMetric[]
  >({
    queryKey: [COMPANY_STATISTICS],
    queryFn: getCompanyStatistics,
    select: (response) => response.data ?? response.data,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: ERefetchType.ALWAYS,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center border-b border-b-[#F5F7F9] p-[24px]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#466AFB] border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="border-b border-b-[#F5F7F9] p-[16px]">
        <p className="text-[14px] text-red-600">
          {error?.message ?? translation.dashboard.ErrorStatistics}
        </p>
      </div>
    );
  }

  if (!metrics?.length) {
    return (
      <div className="border-b border-b-[#F5F7F9] p-[16px]">
        <p className="text-[14px] text-[#292D32]">
          {translation.dashboard.EmptyStatistics}
        </p>
      </div>
    );
  }

  const sortedMetrics = [...(metrics ?? [])].sort(
    (firstElement, secondElement) => {
      if (firstElement.id === OUTGOING_INVOICES_ID) return -1;
      if (secondElement.id === OUTGOING_INVOICES_ID) return 1;
      return 0;
    }
  );

  return (
    <div className="grid grid-cols-1 gap-[8px] border-b border-b-[#F5F7F9] p-[16px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      {sortedMetrics?.map((metric) => (
        <MetricCard
          key={metric?.id}
          metric={metric}
          translation={translation}
        />
      ))}
    </div>
  );
};

export default Statistics;
